/* Balmohk · <bm-product>: comportamiento propio de la ficha (sections/bm-product.liquid).
   No reemplaza la lógica de Horizon: el selector (variant-picker) y el formulario (product-form-component)
   siguen haciendo el cambio de variante, la URL ?variant= y el agregado al carrito. Esto agrega:
   - sincronizar precio y referencia con la variante elegida (con el HTML que ya trae Horizon);
   - el rótulo «Seleccionar talla» del desplegable y no agregar sin talla elegida;
   - compartir la URL de la variante que se está mirando;
   - pestañas y la barra de compra fija;
   - muestras de color que eligen el valor en el <select> de Horizon;
   - el respaldo de «Completa el look» (relacionados) cuando no hay complementarios;
   - el texto del botón de compra de la sección (la tienda corre en otro idioma que el del Figma);
   - «Avisame cuando haya stock»: con la variante elegida agotada, el botón negro en lugar de «Agotado» y el modal
     que registra el correo (formulario de clientes de Shopify por fetch, con el token del hCaptcha);
   - al agregar: el botón cargando y, con el producto ya en el carrito, el carrito lateral abierto. */
import { StandardEvents } from '@shopify/events';
import { viewTransition } from '@theme/utilities';

const SIZE_NAMES = /^(talla|talle|tallas|talles|size|sizes|tamaño|medida)$/i;

/* El mismo camino que «Shop the look» de la Home (sections/bm-shop-the-look.liquid, balmohk#29): el carrito lateral
   de Horizon o, si el tema usa la página del carrito, esa página */
const openCart = () => {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) drawer.open();
    return;
  }
  /* global Theme: const de snippets/scripts (no cuelga de window) */
  const cartUrl = typeof Theme !== 'undefined' ? Theme.routes?.cart_url : '';
  window.location.assign(cartUrl || '/cart');
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

if (!customElements.get('bm-product')) {
  class BmProduct extends HTMLElement {
    #abort = new AbortController();
    #sizeChosen = new URL(window.location.href).searchParams.has('variant');

    connectedCallback() {
      const { signal } = this.#abort;
      const section = this.closest('.shopify-section') || this;

      section.addEventListener(StandardEvents.productSelect, (event) => this.#onProductSelect(event), { signal });
      this.addEventListener('change', (event) => this.#onChange(event), { signal });
      // Captura en window: Horizon escucha on:click en document (captura) y pone data-added (el check);
      // desde window se corre antes que él y se frena el agregado sin talla
      window.addEventListener('click', (event) => this.#guardAddToCart(event), { capture: true, signal });

      this.mobile = window.matchMedia('(max-width: 749px)');
      this.mobile.addEventListener?.('change', () => this.#paintAddLabel(), { signal });
      this.addEventListener('click', (event) => this.#onSwatch(event), { signal });
      this.querySelector('[data-bm-share]')?.addEventListener('click', () => this.#share(), { signal });
      this.#setupTabs(signal);
      this.#setupGuide(signal);
      this.#setupZoom(signal);
      this.#setupSizeList(signal);
      this.#setupBar(signal);
      this.#setupNotify(signal);
      // Horizon avisa el agregado con cart:lines-update desde su product-form-component (burbujea hasta acá)
      this.addEventListener(StandardEvents.cartLinesUpdate, (event) => this.#onAdd(event), { signal });
      this.#paintLabels();
      this.#loadLook();
    }

    disconnectedCallback() {
      this.#abort.abort();
      this.observer?.disconnect();
    }

    /* ── Variante ───────────────────────────────────────────── */
    async #onProductSelect(event) {
      if (event.target instanceof Element && event.target.closest('product-card')) return;
      try {
        const { detail } = await event.promise;
        const html = detail?.html;
        if (html) {
          for (const element of this.querySelectorAll('[data-bm-sync]')) {
            const fresh = html.querySelector(`[data-bm-sync="${element.dataset.bmSync}"]`);
            if (fresh) element.innerHTML = fresh.innerHTML;
          }
        }
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn(error);
      }
      // Horizon re-dibuja el selector y el botón: se vuelven a poner los rótulos
      requestAnimationFrame(() => this.#paintLabels());
    }

    /* ── Muestras de color ──────────────────────────────────── */
    #onSwatch(event) {
      const swatch = event.target instanceof Element ? event.target.closest('.bm-pdp-swatches__swatch') : null;
      if (!swatch || swatch.getAttribute('aria-checked') === 'true') return;
      const group = swatch.closest('.bm-pdp-swatches');
      const name = group?.dataset.optionName;
      const select = name
        ? [...this.querySelectorAll('.bm-pdp__picker select')].find((el) => el.name === `options[${name}]`)
        : null;
      if (!select) return;
      for (const other of group.querySelectorAll('.bm-pdp-swatches__swatch')) {
        other.setAttribute('aria-checked', String(other === swatch));
      }
      this.#keepSizeAvailable(name, swatch.dataset.value);
      select.value = swatch.dataset.value;
      this.#pointToVariant(select);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Horizon arma ?variant= con el data-variant-id de la <option> elegida, que el servidor calculó con la
       talla de antes (Navy Blue / 36 no existe → borra ?variant=): se le pone el id de la combinación real. */
    #pointToVariant(select) {
      const option = select.selectedOptions[0];
      const data = this.#variants();
      if (!option || !data) return;
      const current = {};
      for (const other of this.querySelectorAll('.bm-pdp__picker select')) {
        const selected = other.selectedOptions[0];
        if (selected?.dataset.optionName) current[selected.dataset.optionName] = selected.value;
      }
      const variant = data.variants.find((item) =>
        data.options.every((name, i) => current[name] === undefined || item.options[i] === current[name])
      );
      if (variant?.id) option.dataset.variantId = String(variant.id);
    }

    #variants() {
      try {
        return JSON.parse(this.querySelector('[data-bm-variants]')?.textContent || 'null');
      } catch {
        return null;
      }
    }

    /* ── Lista de tallas propia: muestra «Agotado» y «Pocas unidades»; elige en el <select> de Horizon ─ */
    #setupSizeList(signal) {
      this.addEventListener(
        'click',
        (event) => {
          if (!(event.target instanceof Element) || event.target.closest('[data-bm-size-list]')) return;
          const wrapper = event.target.closest('.bm-pdp__picker .variant-option__select-wrapper[data-bm-sizes]');
          if (!wrapper) return;
          event.preventDefault();
          if (this.sizeList?.parentElement === wrapper) this.#closeSizeList();
          else this.#openSizeList(wrapper, false, event.detail === 0);
        },
        { signal }
      );
      // Teclado sobre el <select>: abre la lista en vez del desplegable del sistema
      this.addEventListener(
        'keydown',
        (event) => {
          const select = event.target;
          if (!(select instanceof HTMLSelectElement) || select !== this.#sizeSelect()) return;
          if (!['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          const wrapper = select.closest('.variant-option__select-wrapper');
          if (wrapper) this.#openSizeList(wrapper, false, true);
        },
        { signal }
      );
      document.addEventListener(
        'click',
        (event) => {
          if (this.sizeList && event.target instanceof Node && !this.sizeList.parentElement?.contains(event.target)) {
            this.#closeSizeList();
          }
        },
        { signal }
      );
    }

    #sizeStatus(value) {
      const data = this.#variants();
      const select = this.#sizeSelect();
      if (!data || !select) return '';
      const current = {};
      for (const other of this.querySelectorAll('.bm-pdp__picker select')) {
        const option = other.selectedOptions[0];
        if (option?.dataset.optionName) current[option.dataset.optionName] = option.value;
      }
      const sizeName = select.selectedOptions[0]?.dataset.optionName || '';
      const variant = data.variants.find((item) =>
        data.options.every((name, i) =>
          name === sizeName ? item.options[i] === value : current[name] === undefined || item.options[i] === current[name]
        )
      );
      // «missing»: la combinación no existe (Navy Blue / XS); se muestra como agotada pero nunca se elige
      if (!variant) return 'missing';
      if (!variant.available) return 'sold-out';
      return variant.low ? 'low' : '';
    }

    #openSizeList(anchor, up = false, keyboard = false) {
      const select = this.#sizeSelect();
      if (!select) return;
      this.#closeSizeList();
      const list = document.createElement('ul');
      list.className = `bm-pdp-sizes${up ? ' bm-pdp-sizes--up' : ''}`;
      list.setAttribute('role', 'listbox');
      list.tabIndex = -1;
      list.setAttribute('aria-label', select.selectedOptions[0]?.dataset.optionName || '');
      list.dataset.bmSizeList = '';
      for (const option of select.options) {
        const status = this.#sizeStatus(option.value);
        const item = document.createElement('li');
        item.className = 'bm-pdp-sizes__option';
        item.setAttribute('role', 'option');
        item.tabIndex = -1;
        item.dataset.value = option.value;
        item.setAttribute('aria-selected', String(this.#sizeChosen && option.value === select.value));
        // Con el aviso de stock la talla agotada se elige (y el botón pasa a «Avisame cuando haya stock», 63:9563);
        // sin él, o si la combinación no existe, queda deshabilitada, como antes
        if (status === 'sold-out' && this.#notifyOn()) item.setAttribute('data-sold-out', 'true');
        else if (status === 'sold-out' || status === 'missing') item.setAttribute('aria-disabled', 'true');
        const value = document.createElement('span');
        value.textContent = option.value;
        item.append(value);
        const note = status === 'sold-out' || status === 'missing' ? this.dataset.soldOut : status === 'low' ? this.dataset.lowStock : '';
        if (note) {
          // El estado va pegado a la talla (dentro de su span): la talla sigue centrada en la fila
          const label = document.createElement('span');
          label.className = `bm-pdp-sizes__status bm-pdp-sizes__status--${status === 'missing' ? 'sold-out' : status}`;
          label.textContent = note;
          value.append(label);
        }
        list.append(item);
      }
      list.addEventListener('click', (event) => {
        const item = event.target instanceof Element ? event.target.closest('.bm-pdp-sizes__option') : null;
        if (item) this.#chooseSize(item, event.detail === 0);
      });
      list.addEventListener('keydown', (event) => this.#onSizeListKey(event));
      anchor.append(list);
      this.sizeList = list;
      const items = [...list.children];
      const start =
        items.find((item) => item.getAttribute('aria-selected') === 'true') ||
        items.find((item) => !item.hasAttribute('aria-disabled') && !item.hasAttribute('data-sold-out')) ||
        items.find((item) => !item.hasAttribute('aria-disabled'));
      // Con el teclado el foco va a la talla; con clic o tap, a la lista: si no, Chrome marca la fila con
      // :focus-visible y queda resaltada sin el mouse encima (53:4967). Las flechas siguen andando desde la lista.
      if (keyboard) start?.focus({ preventScroll: true });
      else list.focus({ preventScroll: true });
      // La del selector se abre debajo de él: entera a la vista (en mobile el selector queda cerca
      // del pie de la pantalla y sólo asomaba la primera talla). La de la barra fija ya sube desde la barra.
      if (!up) list.scrollIntoView({ block: 'nearest' });
      start?.scrollIntoView({ block: 'nearest' });
    }

    #closeSizeList(focus = false) {
      if (!this.sizeList) return;
      this.sizeList.remove();
      this.sizeList = null;
      if (focus) this.#sizeSelect()?.focus({ preventScroll: true });
    }

    #onSizeListKey(event) {
      const items = [...(this.sizeList?.children || [])];
      const index = items.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        items[Math.min(items.length - 1, Math.max(0, index + step))]?.focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (items[index]) this.#chooseSize(items[index], true);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.#closeSizeList(true);
      } else if (event.key === 'Tab') {
        this.#closeSizeList();
      }
    }

    // Con teclado el foco vuelve al <select>; con clic o tap no: si no, matchea :focus-visible y el wrapper
    // dibuja el anillo de foco además del borde (198:10989, 198:16745)
    #chooseSize(item, keyboard = false) {
      if (item.hasAttribute('aria-disabled')) return;
      const select = this.#sizeSelect();
      if (!select) return;
      this.#closeSizeList(keyboard);
      if (select.value === item.dataset.value && this.#sizeChosen) return;
      select.value = item.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Sin talla elegida, la talla oculta del selector de Horizon pasa a la primera con stock del color nuevo:
       si no, queda una combinación que no existe (Navy Blue / 36) y el botón dice «Unavailable». */
    #keepSizeAvailable(colorName, colorValue) {
      const sizeSelect = this.#sizeSelect();
      if (this.#sizeChosen || !sizeSelect) return;
      const data = this.#variants();
      if (!data) return;
      const colorIndex = data.options.indexOf(colorName);
      const sizeIndex = data.options.indexOf(sizeSelect.selectedOptions[0]?.dataset.optionName || '');
      if (colorIndex < 0 || sizeIndex < 0) return;
      const withColor = data.variants.filter((variant) => variant.options[colorIndex] === colorValue);
      if (withColor.some((variant) => variant.available && variant.options[sizeIndex] === sizeSelect.value)) return;
      const pick = withColor.find((variant) => variant.available) || withColor[0];
      if (!pick) return;
      // Horizon arma la variante con option[selected]: se mueve el atributo además del valor
      for (const option of sizeSelect.options) {
        const match = option.value === pick.options[sizeIndex];
        option.selected = match;
        option.toggleAttribute('selected', match);
      }
    }

    /* ── Completa el look: relacionados si no hay complementarios ─ */
    async #loadLook() {
      const url = this.dataset.lookUrl;
      const lists = [...this.querySelectorAll('[data-bm-look-pending]')];
      if (!url || !lists.length) return;
      let items = [];
      try {
        const response = await fetch(url);
        if (response.ok) {
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          items = [...html.querySelectorAll('[data-bm-look-items] > li')];
        }
      } catch {
        /* sin red: queda vacío */
      }
      for (const list of lists) {
        list.removeAttribute('data-bm-look-pending');
        if (!items.length) {
          // Vacío honesto: sin productos no se dibuja el bloque
          (list.closest('.bm-pdp__look-band') || list).remove();
          continue;
        }
        list.replaceChildren(...items.map((item) => item.cloneNode(true)));
      }
    }

    /* ── Texto del botón de compra ──────────────────────────── */
    #paintAddLabel() {
      const button = this.querySelector('.bm-pdp__form button[name="add"]');
      if (!button || button.disabled) return;
      const label = (this.mobile?.matches && this.dataset.addLabelMobile) || this.dataset.addLabel;
      if (!label) return;
      const node = [...button.childNodes].find((child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim());
      if (node && node.textContent.trim() !== label) node.textContent = label;
    }

    #onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement) || !target.closest('.variant-option--dropdowns')) return;
      if (this.#isSize(target)) this.#sizeChosen = true;
      const wrapper = target.closest('.variant-option__select-wrapper');
      wrapper?.removeAttribute('data-bm-error');
      this.#paintLabels();
    }

    #isSize(select) {
      const option = select.selectedOptions[0] || select.options[0];
      return SIZE_NAMES.test((option?.dataset.optionName || '').trim());
    }

    #sizeSelect() {
      return [...this.querySelectorAll('.bm-pdp__picker .variant-option--dropdowns select')].find((select) =>
        this.#isSize(select)
      );
    }

    #paintLabels() {
      for (const select of this.querySelectorAll('.bm-pdp__picker .variant-option--dropdowns select')) {
        const wrapper = select.closest('.variant-option__select-wrapper');
        if (!wrapper || wrapper.hasAttribute('data-bm-error')) continue;
        const option = select.selectedOptions[0];
        const name = option?.dataset.optionName || '';
        const value = option?.value || '';
        const isSize = this.#isSize(select);
        wrapper.toggleAttribute('data-bm-sizes', isSize);
        // La talla elegida se muestra sola («M»), sin el nombre de la opción (198:10989)
        let label = isSize ? value : `${name}: ${value}`;
        if (isSize && !this.#sizeChosen) label = this.dataset.sizePlaceholder || label;
        wrapper.setAttribute('data-bm-label', label);
      }
      this.#paintNotify();
      this.#paintAddLabel();
      this.#paintBar();
    }

    #guardAddToCart(event) {
      const button = event.target instanceof Element ? event.target.closest('button[name="add"]') : null;
      if (!button || !this.querySelector('.bm-pdp__form')?.contains(button)) return;
      const select = this.#sizeSelect();
      if (!select || this.#sizeChosen) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#askForSize(select);
    }

    #askForSize(select) {
      const wrapper = select.closest('.variant-option__select-wrapper');
      if (wrapper) {
        wrapper.setAttribute('data-bm-error', '');
        wrapper.setAttribute('data-bm-label', this.dataset.sizeError || '');
      }
      // El error va con el selector cerrado (110:12203)
      this.#closeSizeList();
    }

    /* ── Agregar al carrito (Asana 1219786375763923) ────────── */
    /* Antes el POST daba 200 y sólo cambiaba el contador del header: «Abrir el carrito al agregar» está apagado en este
       tema y el Figma no dibuja ese estado. Mientras se agrega, el botón (y el de la barra fija) queda deshabilitado con
       aria-busy y el mismo tamaño; con el agregado confirmado se abre el carrito lateral con el producto ya adentro.
       Si falla (sin stock, error), el botón vuelve y Horizon muestra el motivo debajo, como antes. El foco lo toma el
       cajón al abrirse y theme-drawer lo devuelve, al cerrarse, al botón con el que se agregó. */
    #onAdd(event) {
      const form = this.querySelector('.bm-pdp__form');
      // Sólo el formulario de la ficha: la compra rápida de «Completa el look» sigue su camino
      if (event.action !== 'add' || !event.promise || !(event.target instanceof Node) || !form?.contains(event.target)) return;
      const variantId = form.querySelector('input[name="id"]')?.value || '';
      const trigger = this.addTrigger || form.querySelector('button[name="add"]');
      this.#addBusy(true);
      event.promise
        .then(async (result) => {
          if (result?.detail?.didError) {
            this.#addDone(trigger);
            return;
          }
          await this.#cartReady(variantId);
          this.#addDone(trigger);
          openCart();
        })
        .catch(() => this.#addDone(trigger));
    }

    #addBusy(on) {
      for (const button of [
        this.querySelector('.bm-pdp__form button[name="add"]'),
        this.querySelector('[data-bm-bar-button]'),
      ]) {
        if (!button) continue;
        button.disabled = on;
        if (on) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      }
    }

    #addDone(trigger) {
      this.#addBusy(false);
      this.#paintBar();
      // Deshabilitado pierde el foco (y Safari no lo da al hacer clic): vuelve al botón antes de abrir el carrito, así
      // theme-drawer lo toma como disparador y lo devuelve ahí al cerrar. Si el foco ya está en otro lado, no se mueve.
      const button = trigger?.isConnected ? trigger : this.querySelector('.bm-pdp__form button[name="add"]');
      const active = document.activeElement;
      if (!active || active === document.body || active === button) button?.focus({ preventScroll: true });
    }

    /* El cajón se re-dibuja con la respuesta del agregado (cart-items-component, con una transición de vista si estaba
       vacío): se espera a que tenga la línea de la variante y a que termine la transición, con tope, para abrirlo con
       el producto adentro y no vacío. Sin cajón (carrito en página) no hay que esperar. */
    async #cartReady(variantId) {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer || !variantId) return;
      const limit = performance.now() + 1500;
      while (!drawer.querySelector(`[data-key^="${variantId}:"]`) && performance.now() < limit) await wait(30);
      if (viewTransition.current) await Promise.race([viewTransition.current, wait(1000)]);
    }

    /* ── Compartir ──────────────────────────────────────────── */
    async #share() {
      const url = window.location.href;
      const title = this.querySelector('.bm-pdp__title')?.textContent?.trim() || document.title;
      if (navigator.share) {
        try {
          await navigator.share({ title, url });
        } catch {
          /* cancelado */
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        const label = this.querySelector('[data-bm-share-label]');
        if (!label) return;
        const original = label.textContent;
        label.textContent = this.dataset.copied || original;
        setTimeout(() => (label.textContent = original), 2000);
      } catch {
        window.prompt('', url);
      }
    }

    /* ── Pestañas ───────────────────────────────────────────── */
    #setupTabs(signal) {
      const root = this.querySelector('[data-bm-tabs]');
      if (!root) return;
      const tabs = [...root.querySelectorAll('[aria-controls]')];
      for (const tab of tabs) {
        tab.addEventListener(
          'click',
          () => {
            const open = tab.getAttribute('aria-expanded') !== 'true';
            for (const other of tabs) {
              other.setAttribute('aria-expanded', String(open && other === tab));
              const panel = document.getElementById(other.getAttribute('aria-controls'));
              if (panel) panel.hidden = !(open && other === tab);
            }
          },
          { signal }
        );
      }
    }

    /* ── Guía de tallas: la abren el link junto a la talla y la pestaña 3 ── */
    #setupGuide(signal) {
      const dialog = this.querySelector('[data-bm-guide]');
      if (!(dialog instanceof HTMLDialogElement)) return;
      // <dialog> nativo: Escape y el foco de vuelta al disparador los da el navegador; clic afuera cierra
      for (const opener of this.querySelectorAll(`[data-bm-dialog-open="${dialog.id}"]`)) {
        opener.addEventListener('click', () => dialog.showModal(), { signal });
      }
      dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === dialog || (event.target instanceof Element && event.target.closest('[data-bm-dialog-close]'))) {
            dialog.close();
          }
        },
        { signal }
      );

      // CM / INCHES: los rangos en cm («78 - 82») se pasan a pulgadas con un decimal
      const units = [...dialog.querySelectorAll('[data-bm-guide-unit]')];
      const cells = [...dialog.querySelectorAll('[data-bm-guide-cm]')];
      const toInches = (text) =>
        text.replace(/\d+(?:[.,]\d+)?/g, (number) => {
          const inches = parseFloat(number.replace(',', '.')) / 2.54;
          return (Math.round(inches * 10) / 10).toLocaleString(document.documentElement.lang || undefined);
        });
      for (const unit of units) {
        unit.addEventListener(
          'click',
          () => {
            const inches = unit.dataset.bmGuideUnit === 'in';
            for (const other of units) other.setAttribute('aria-pressed', String(other === unit));
            for (const cell of cells) {
              const cm = cell.dataset.bmGuideCm || '';
              cell.textContent = inches ? toInches(cm) : cm;
            }
          },
          { signal }
        );
      }
    }

    /* ── Zoom: la foto principal o de la grilla a pantalla completa ─ */
    #setupZoom(signal) {
      const dialog = this.querySelector('[data-bm-zoom]');
      const image = dialog?.querySelector('[data-bm-zoom-image]');
      if (!(dialog instanceof HTMLDialogElement) || !(image instanceof HTMLImageElement)) return;
      const photos = () => [...this.querySelectorAll('.bm-pdp__hero img, .bm-pdp__photo img')];
      const next = dialog.querySelector('[data-bm-zoom-next]');
      let index = 0;
      const show = (i) => {
        const list = photos();
        if (!list.length) return;
        index = (i + list.length) % list.length;
        const source = list[index];
        // La medida más grande del srcset
        const largest = (source.srcset || '').split(',').pop()?.trim().split(/\s+/)[0];
        image.src = largest || source.currentSrc || source.src;
        image.alt = source.alt;
        if (next) next.hidden = list.length < 2;
        // Cada foto arranca desde arriba (188:9548): si no, al reabrir queda el scroll de la anterior
        dialog.scrollTop = 0;
      };
      this.addEventListener(
        'click',
        (event) => {
          const target = event.target instanceof Element ? event.target.closest('.bm-pdp__hero img, .bm-pdp__photo img') : null;
          if (!target) return;
          show(photos().indexOf(target));
          dialog.showModal();
          // Cerrado no tiene caja y el scrollTop de show() no corre: se vuelve arriba ya abierto
          dialog.scrollTop = 0;
          // El foco va al dialog, no a «cerrar»: si no, cerrar abre con el anillo de :focus-visible (188:9550)
          dialog.focus({ preventScroll: true });
        },
        { signal }
      );
      next?.addEventListener('click', () => show(index + 1), { signal });
      // Cierra con «Cerrar», con la foto y con el fondo blanco fuera de ella (decisión de Pow, Asana
      // 1219791416194085): el fondo es el propio dialog. Del fondo sólo si el clic también empezó ahí: arrastrar
      // desde la foto hacia afuera da un click en el dialog y no tiene que cerrar. La barra de scroll de desktop
      // queda fuera del clientWidth y tampoco cierra. El foco vuelve solo (dialog.close) a donde estaba al abrir
      let pressedOnBackdrop = false;
      dialog.addEventListener('pointerdown', (event) => (pressedOnBackdrop = event.target === dialog), { signal });
      dialog.addEventListener(
        'click',
        (event) => {
          const onBackdrop =
            event.target === dialog &&
            pressedOnBackdrop &&
            event.clientX - dialog.getBoundingClientRect().left < dialog.clientWidth;
          if (
            onBackdrop ||
            event.target === image ||
            (event.target instanceof Element && event.target.closest('[data-bm-zoom-close]'))
          ) {
            dialog.close();
          }
        },
        { signal }
      );
      dialog.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowRight') show(index + 1);
          if (event.key === 'ArrowLeft') show(index - 1);
        },
        { signal }
      );
      // Swipe horizontal en mobile: a la izquierda la siguiente, a la derecha la anterior (QA pdp-info-14). Sólo si
      // el gesto es sobre todo horizontal: el vertical sigue siendo el scroll del dialog
      let touchStart = null;
      dialog.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.touches.length === 1 ? event.touches[0] : null;
          touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
        },
        { passive: true, signal }
      );
      dialog.addEventListener(
        'touchend',
        (event) => {
          const touch = event.changedTouches[0];
          if (!touchStart || !touch) return;
          const dx = touch.clientX - touchStart.x;
          const dy = touch.clientY - touchStart.y;
          touchStart = null;
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5 || photos().length < 2) return;
          show(dx < 0 ? index + 1 : index - 1);
        },
        { passive: true, signal }
      );
      dialog.addEventListener('touchcancel', () => (touchStart = null), { passive: true, signal });
    }

    /* ── Avisame cuando haya stock (63:9732 / 63:9944) ─────────── */
    #notifyOn() {
      return Boolean(this.querySelector('[data-bm-notify-open]'));
    }

    /* Variante de la combinación elegida en los <select> (al instante, sin esperar la respuesta de Horizon); con
       opciones sin <select> (un solo valor) desempata el id del formulario de compra */
    #currentVariant() {
      const data = this.#variants();
      if (!data) return null;
      const current = {};
      for (const select of this.querySelectorAll('.bm-pdp__picker select')) {
        const option = select.selectedOptions[0];
        if (option?.dataset.optionName) current[option.dataset.optionName] = option.value;
      }
      const matches = data.variants.filter((item) =>
        data.options.every((name, i) => current[name] === undefined || item.options[i] === current[name])
      );
      if (matches.length === 1) return matches[0];
      const id = Number(this.querySelector('.bm-pdp__form input[name="id"]')?.value);
      return matches.find((item) => item.id === id) || null;
    }

    #paintNotify() {
      if (!this.#notifyOn()) return;
      const variant = this.#currentVariant();
      // Combinación inexistente: queda «No disponible» de Horizon; sólo una variante real agotada ofrece el aviso
      this.toggleAttribute('data-bm-notify', Boolean(variant && !variant.available));
    }

    // «Jean DENIM FLARE FIT — Light Blue, talla 38»: las opciones con más de un valor, la talla con su rótulo
    #describeVariant(variant) {
      const data = this.#variants();
      const title = this.querySelector('.bm-pdp__title')?.textContent?.trim() || '';
      if (!data) return title;
      const parts = [];
      data.options.forEach((name, i) => {
        const values = new Set(data.variants.map((item) => item.options[i]));
        if (values.size < 2) return;
        const value = variant.options[i];
        parts.push(SIZE_NAMES.test(name.trim()) ? `${this.dataset.notifySize || name} ${value}` : value);
      });
      return parts.length ? `${title} — ${parts.join(', ')}` : title;
    }

    #setupNotify(signal) {
      const dialog = this.querySelector('[data-bm-notify-dialog]');
      const form = dialog?.querySelector('form');
      if (!(dialog instanceof HTMLDialogElement) || !form) return;
      this.notifyDialog = dialog;
      this.querySelector('[data-bm-notify-open]')?.addEventListener('click', () => this.#onNotifyClick(), { signal });
      // Cerrar con la X o tocando afuera (el panel ocupa todo el <dialog>: el clic en el propio <dialog> es el velo)
      dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === dialog || (event.target instanceof Element && event.target.closest('[data-bm-notify-close]'))) {
            dialog.close();
          }
        },
        { signal }
      );
      // En captura desde el <dialog>: corre antes que el hCaptcha de Shopify, que escucha el submit del formulario
      dialog.addEventListener('submit', (event) => this.#onNotifySubmit(event), { capture: true, signal });
      form.addEventListener('input', () => this.#notifyError(''), { signal });
      // El hCaptcha de Shopify toma form.submit de la instancia al engancharse y lo llama con el token
      form.submit = () => this.#sendNotify(form);

      // Vuelta de un envío por la página de desafío de Shopify: la ficha se abre con el modal en el estado devuelto
      const body = dialog.querySelector('[data-bm-notify-state]');
      if (body && body.dataset.bmNotifyState !== 'form' && window.location.hash === '#' + form.id) {
        if (body.dataset.bmNotifyState === 'success') this.#notifyStep('success');
        requestAnimationFrame(() => this.#openNotify(true));
      }
    }

    // Igual que «Añadir»: sin talla elegida se pide la talla en el selector (110:12203)
    #onNotifyClick() {
      const select = this.#sizeSelect();
      if (select && select.options.length > 1 && !this.#sizeChosen) {
        this.#askForSize(select);
        return;
      }
      this.#openNotify();
    }

    #openNotify(keepState = false) {
      const dialog = this.notifyDialog;
      const variant = this.#currentVariant();
      if (!dialog || !variant || dialog.open) return;
      const product = dialog.querySelector('[data-bm-notify-product]');
      if (product) product.textContent = this.#describeVariant(variant);
      // Etiquetas del cliente: la general, la de la variante (la que busca Flow) y la del producto
      const tag = dialog.dataset.tag || 'aviso-stock';
      const tags = dialog.querySelector('[data-bm-notify-tags]');
      if (tags instanceof HTMLInputElement) {
        tags.value = [tag, tag + '-' + variant.id, this.dataset.productHandle ? tag + '-' + this.dataset.productHandle : '']
          .filter(Boolean)
          .join(', ');
      }
      // Otra variante: el modal vuelve al formulario aunque la anterior ya se haya registrado
      if (!keepState && dialog.dataset.variantId !== String(variant.id)) {
        this.#notifyStep('form');
        this.#notifyError('');
      }
      dialog.dataset.variantId = String(variant.id);
      dialog.showModal();
      this.#notifyFocus();
    }

    // Al abrir y después de enviar: el correo si hay formulario, «cerrar» si ya está la confirmación
    #notifyFocus() {
      const dialog = this.notifyDialog;
      const email = dialog?.querySelector('[data-bm-notify-email]');
      if (dialog?.querySelector('[data-bm-notify-step="form"]')?.hidden || !(email instanceof HTMLInputElement)) {
        dialog?.querySelector('[data-bm-notify-close]')?.focus();
      } else {
        email.focus();
      }
    }

    #notifyStep(step) {
      const dialog = this.notifyDialog;
      if (!dialog) return;
      const form = dialog.querySelector('[data-bm-notify-step="form"]');
      const success = dialog.querySelector('[data-bm-notify-success]');
      if (form) form.hidden = step === 'success';
      // La confirmación se escribe en el role="status" ya presente: así se anuncia
      if (success) success.textContent = step === 'success' ? dialog.dataset.success || '' : '';
    }

    #notifyError(message) {
      const dialog = this.notifyDialog;
      const error = dialog?.querySelector('[data-bm-notify-error]');
      const email = dialog?.querySelector('[data-bm-notify-email]');
      if (!error || !email) return;
      if (!message && error.hidden) return;
      error.textContent = message;
      error.hidden = !message;
      if (message) email.setAttribute('aria-invalid', 'true');
      else email.removeAttribute('aria-invalid');
    }

    /* Envío por fetch, sin salir de la ficha. El formulario de clientes lo custodia el hCaptcha de Shopify (se engancha
       al enfocar el formulario): escucha el submit, pide el token (invisible) y llama a form.submit(), que al
       engancharse tomó de la instancia. Por eso form.submit es el envío por fetch: llega con el token puesto. Sin
       hCaptcha enganchado (protección apagada o todavía cargando) el fetch sale directo. */
    #onNotifySubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const email = form.querySelector('[data-bm-notify-email]');
      const stop = () => {
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      if (this.notifySending || !(email instanceof HTMLInputElement)) {
        stop();
        return;
      }
      email.value = email.value.trim();
      if (!email.value || !email.checkValidity()) {
        // Sin datos válidos no llega al hCaptcha (que pediría igual el desafío)
        stop();
        this.#notifyError(email.value ? this.dataset.notifyErrorEmail : this.dataset.notifyErrorRequired);
        email.focus();
        return;
      }
      this.#notifyBusy(true);
      if (form.hasAttribute('data-hcaptcha-bound')) return;
      stop();
      this.#sendNotify(form);
    }

    #notifyBusy(on) {
      const dialog = this.notifyDialog;
      if (!dialog) return;
      this.notifySending = on;
      dialog.querySelector('[type="submit"]')?.toggleAttribute('aria-busy', on);
      window.clearTimeout(this.notifyTimer);
      if (on) {
        // Si el hCaptcha muestra un desafío va detrás de la capa superior del modal (inerte): mientras se envía, el
        // modal pasa a no modal y el desafío queda a mano. Sin respuesta en 90 s (desafío abandonado), error.
        if (dialog.matches(':modal')) {
          dialog.close();
          dialog.show();
        }
        this.notifyTimer = window.setTimeout(() => this.#notifyDone('error'), 90000);
      } else if (dialog.open && !dialog.matches(':modal')) {
        dialog.close();
        dialog.showModal();
      }
    }

    async #sendNotify(form) {
      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new URLSearchParams(new FormData(form)),
          headers: { Accept: 'text/html' },
        });
        // Shopify pide su página de desafío: se envía arriba y la ficha vuelve con el modal abierto
        if (response.url.includes('/challenge')) {
          window.clearTimeout(this.notifyTimer);
          HTMLFormElement.prototype.submit.call(form);
          return;
        }
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.getElementById(form.id)?.querySelector('[data-bm-notify-state]');
        if (!response.ok || !fresh) {
          this.#notifyDone('error');
          return;
        }
        const state = fresh.dataset.bmNotifyState;
        const message = fresh.querySelector('[data-bm-notify-error]')?.textContent?.trim() || '';
        // Correo que ya es cliente: Shopify no lo registra de nuevo (vuelve sin éxito ni error, o con «taken»).
        // Se confirma igual; las etiquetas no se suman (limitación del formulario de clientes).
        const taken =
          state === 'form' ||
          (fresh.hasAttribute('data-bm-notify-email-error') && /taken|already|ya (está|existe)|registrad|en uso/i.test(message));
        this.#notifyDone(state === 'success' || taken ? 'success' : 'error', message);
      } catch {
        this.#notifyDone('error');
      }
    }

    #notifyDone(result, message = '') {
      this.#notifyBusy(false);
      this.#notifyError(result === 'success' ? '' : message || this.dataset.notifyErrorGeneric || '');
      if (result === 'success') this.#notifyStep('success');
      this.#notifyFocus();
    }

    /* ── Barra de compra fija ───────────────────────────────── */
    #setupBar(signal) {
      this.bar = this.querySelector('[data-bm-bar]');
      const anchor = this.querySelector('.bm-pdp__form');
      if (!this.bar || !anchor || !('IntersectionObserver' in window)) return;

      let footerVisible = false;
      let frame = 0;
      // Se mide el formulario en cada aviso: un salto directo (1600 → 0) no lo hace cruzar el viewport.
      // El límite no es y=0 sino el borde inferior del header fijo (con la barra de anuncios: 101 en desktop, 78 en
      // mobile), leído en vivo: si no, mientras el header tapa los botones no se ve ningún botón de compra (QA barra-15)
      const update = () => {
        frame = 0;
        const header = document.querySelector('.bm-header');
        const covered = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const pastButtons = anchor.getBoundingClientRect().bottom < covered;
        this.#toggleBar(pastButtons && !footerVisible);
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };

      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target !== anchor) footerVisible = entry.isIntersecting;
        }
        update();
      });
      this.observer.observe(anchor);
      const footer = document.querySelector('.bm-footer') || document.querySelector('footer');
      if (footer) this.observer.observe(footer);
      // Cruzar el borde del header no lo avisa el observer: se mide al scrollear. En captura sobre document porque en
      // desktop el scroll de Horizon es el de .page-wrapper, que no llega a window (como el header, bm-header.liquid)
      document.addEventListener('scroll', schedule, { capture: true, passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      signal.addEventListener('abort', () => cancelAnimationFrame(frame));

      this.querySelector('[data-bm-bar-button]')?.addEventListener('click', () => this.#onBarClick(), { signal });
      document.addEventListener(
        'keydown',
        (event) => {
          // El Escape de un diálogo (el carrito que abrió la barra, la guía, el zoom) lo cierra a él y no esconde la
          // barra: si no, la barra queda inerte y el foco no puede volver a su botón
          if (event.target instanceof Element && event.target.closest('dialog')) return;
          if (event.key === 'Escape' && this.bar.hasAttribute('data-visible')) this.#toggleBar(false);
        },
        { signal }
      );
    }

    #toggleBar(show) {
      if (!this.bar) return;
      if (!show && this.sizeList?.parentElement === this.bar) this.#closeSizeList();
      this.bar.toggleAttribute('data-visible', show);
      this.bar.inert = !show;
    }

    #paintBar() {
      const button = this.querySelector('[data-bm-bar-button]');
      const label = this.querySelector('[data-bm-bar-label]');
      if (!button || !label) return;
      const needsSize = this.#sizeSelect() && !this.#sizeChosen;
      const add = this.querySelector('.bm-pdp__form button[name="add"]');
      const notify = this.hasAttribute('data-bm-notify') ? this.querySelector('[data-bm-notify-open]') : null;
      if (needsSize || (!add && !notify)) {
        label.textContent = this.dataset.sizePlaceholder || label.textContent;
        button.removeAttribute('data-ready');
      } else if (notify) {
        // Variante agotada: la barra ofrece el aviso, como el botón del bloque de compra
        label.textContent = notify.textContent.trim();
        button.toggleAttribute('data-ready', true);
        button.disabled = false;
      } else {
        label.textContent = add.textContent.trim();
        button.toggleAttribute('data-ready', true);
        button.disabled = add.disabled;
      }
    }

    #onBarClick() {
      const select = this.#sizeSelect();
      if (select && !this.#sizeChosen) {
        // La lista se abre hacia arriba desde la barra: en desktop apoyada sobre el botón y con su ancho
        // (110:14284); en mobile el CSS la pasa a todo el ancho, tapando la barra (148:12324)
        if (this.bar && this.sizeList?.parentElement !== this.bar) {
          this.#openSizeList(this.bar, true);
          const button = this.querySelector('[data-bm-bar-button]');
          if (button && this.sizeList) {
            this.sizeList.style.setProperty('--bm-sizes-left', `${button.offsetLeft}px`);
            this.sizeList.style.setProperty('--bm-sizes-width', `${button.offsetWidth}px`);
            this.sizeList.style.setProperty('--bm-sizes-bottom', `${this.bar.clientHeight - button.offsetTop}px`);
          }
        } else {
          this.#closeSizeList();
        }
        return;
      }
      if (this.hasAttribute('data-bm-notify')) {
        this.#openNotify();
        return;
      }
      // Agregado desde la barra: al cerrar el carrito el foco vuelve a su botón (#onAdd lo lee durante el clic)
      this.addTrigger = this.querySelector('[data-bm-bar-button]');
      this.querySelector('.bm-pdp__form button[name="add"]')?.click();
      this.addTrigger = null;
    }
  }

  customElements.define('bm-product', BmProduct);
}
